import { useState, useEffect, useCallback } from 'react';
import useUserDetails from '../hooks/useUserDetails';
import useErrorModal from '../hooks/useErrorModal';
import CustomButton from '../reusable/CustomButton';
import CustomAlert from '../reusable/CustomAlert';
import SelectDropdown from '../reusable/SelectDropdown';
import Panel from '../reusable/Panel';
import Loader from '../reusable/Loader';
import {
  TemplateSurvey,
  Audience,
  createSurvey,
  CreateSurveyRequest,
  getTemplateSurveys,
  getAudiences,
} from '../../services/surveyService';

const CreateSurveySection = () => {
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const { handleOpen: handleOpenErrorModal } = useErrorModal();

  const [templateSurveys, setTemplateSurveys] = useState<TemplateSurvey[]>([]);
  const [audiences, setAudiences] = useState<Audience[]>([]);
  const [isLoadingFormData, setIsLoadingFormData] = useState(true);

  const [templateSurveyId, setTemplateSurveyId] = useState('');
  const [audienceId, setAudienceId] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createSuccess, setCreateSuccess] = useState(false);

  const fetchFormData = useCallback(async () => {
    if (!userDetails) return;

    try {
      setIsLoadingFormData(true);
      const [templatesData, audiencesData] = await Promise.all([
        getTemplateSurveys(userDetails.access_token),
        getAudiences(userDetails.access_token),
      ]);
      setTemplateSurveys(templatesData);
      setAudiences(audiencesData);
    } catch (error: any) {
      console.error('Error fetching form data:', error);
      const errorMessage = error?.response?.data?.detail || 'Failed to fetch form data. Please try again.';
      handleOpenErrorModal(errorMessage, error as Error);
    } finally {
      setIsLoadingFormData(false);
    }
  }, [userDetails, handleOpenErrorModal]);

  useEffect(() => {
    fetchFormData();
  }, [fetchFormData]);

  const handleCreateSurvey = async () => {
    if (!userDetails || !templateSurveyId || !audienceId) {
      return;
    }

    setIsCreating(true);
    setCreateSuccess(false);

    try {
      const requestData: CreateSurveyRequest = {
        template_survey_id: templateSurveyId,
        audience_id: audienceId,
      };

      await createSurvey(userDetails.eoaId, userDetails.access_token, requestData);
      setCreateSuccess(true);
      setTemplateSurveyId('');
      setAudienceId('');

      setTimeout(() => {
        setCreateSuccess(false);
      }, 5000);
    } catch (error: any) {
      console.error('Error creating survey:', error);
      const errorMessage = error?.response?.data?.detail || 'Failed to create survey. Please try again.';
      handleOpenErrorModal(errorMessage, error as Error);
    } finally {
      setIsCreating(false);
    }
  };

  const canCreate = templateSurveyId && audienceId;

  if (isLoadingFormData) {
    return (
      <Panel className="survey-management-panel">
        <h2 className="survey-management-section-title">Create New Survey</h2>
        <Loader />
      </Panel>
    );
  }

  return (
    <Panel className="survey-management-panel">
      <h2 className="survey-management-section-title">Create New Survey</h2>
      {createSuccess && (
        <CustomAlert alertType="success" header="Survey creation initiated">
          The survey is being created. Please check back later for the results. There is no automatic notification when
          the survey is ready.
        </CustomAlert>
      )}
      <div className="survey-management-create-form">
        <SelectDropdown
          label="Template Survey"
          selectedValue={templateSurveyId}
          handleChange={(e) => setTemplateSurveyId(e.target.value)}
          items={templateSurveys.map((t) => ({ label: t.name, value: t.id }))}
          fullWidth={false}
        />
        <SelectDropdown
          label="Audience"
          selectedValue={audienceId}
          handleChange={(e) => setAudienceId(e.target.value)}
          items={audiences.map((a) => ({ label: a.name, value: a.id }))}
          fullWidth={false}
        />
        <CustomButton
          variant="primary"
          buttonText="Create Survey"
          buttonAction={handleCreateSurvey}
          disabled={!canCreate || isCreating}
          loading={isCreating}
        />
      </div>
    </Panel>
  );
};

export default CreateSurveySection;
